<template>
  <div class="pt-2 h-full flex flex-col">
    <!-- Floating Action Bar for bulk actions -->
    <FloatingActionBar
      :selected="rolesPage.selectedRoles"
      :total-items="rolesPage.totalRoles"
      v-iam-disabled="'organization.roles.delete'"
      :showDeleteSelected="true"
      :show-assign="false"
      :showDeleteAll="false"
      :showUpdateStatus="false"
      :showSelectAll="false"
      @delete-selected="rolesPage.handleShowDialogDelete"
    />

    <!-- Mobile Search Field -->
    <div class="px-3 py-2 bg-white border-b mb-2">
      <v-text-field
        outlined
        dense
        hide-details
        placeholder="Search Role"
        v-model="rolesPage.filterRole"
        @input="rolesPage.handleInput"
        clearable
      ></v-text-field>
    </div>

    <!-- Role Form Dialog (Fullscreen on Mobile) -->
    <v-dialog v-model="rolesPage.showForm" fullscreen persistent content-class="dialog-content">
      <v-card class="d-flex flex-column h-full">
        <!-- Dialog Header / Tabs -->
        <ActitiveTabcomponent
          :tabs="rolesPage.mainTabs"
          :defaultTab="'lender'"
          class="flex-grow-1 d-flex flex-column"
        >
          <template #tab-buttons>
            <v-btn icon @click="rolesPage.closeForm">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </template>

          <template #lender>
            <div class="d-flex flex-column form-content pb-16">
              <!-- Help Text -->
              <div
                class="help-text text-start mx-3 my-2 text-xs text-gray-600 bg-blue-50 p-2 rounded"
              >
                <strong>Role:</strong> Defines what this user can access and do.
                <br />
                <strong>Examples:</strong> ADMIN, HR_MANAGER, EMPLOYEE
              </div>

              <!-- Role Name field -->
              <div class="px-3 py-2 bg-white sticky top-0 z-10 border-b">
                <v-text-field
                  autofocus
                  v-model="rolesPage.roleName"
                  label="Role Name"
                  hide-details
                  :error-messages="rolesPage.roleNameValidationRule"
                  outlined
                  dense
                ></v-text-field>
              </div>

              <v-card-text class="mt-2 px-2 flex-grow-1">
                <v-expansion-panels v-model="rolesPage.panel" flat outlined accordion>
                  <!-- 1. Privilege -->
                  <v-expansion-panel class="my-2 border border-gray-200 rounded-lg">
                    <v-expansion-panel-header class="py-2">
                      <span class="font-bold text-sm text-gray-800">Privileges</span>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content class="px-1">
                      <div class="flex justify-end mb-2">
                        <v-btn
                          :color="$vuetify.theme.currentTheme.primary"
                          class="white--text text-xs"
                          @click="rolesPage.toggleAllPrivilegePermissions"
                          small
                          depressed
                        >
                          {{ rolesPage.allPrivilegePermissionsText }}
                        </v-btn>
                      </div>
                      <div class="flex flex-col gap-1">
                        <v-checkbox
                          v-for="(permission, index) in rolesPage.additionalPermissions"
                          :key="index"
                          v-model="permission.checked"
                          :label="permission.value"
                          :color="$vuetify.theme.currentTheme.primary"
                          @click="rolesPage.checkFormChanges"
                          dense
                          hide-details
                          class="mt-1"
                        ></v-checkbox>
                      </div>
                    </v-expansion-panel-content>
                  </v-expansion-panel>

                  <!-- 2. Permissions -->
                  <v-expansion-panel class="my-2 border border-gray-200 rounded-lg">
                    <v-expansion-panel-header class="py-2">
                      <span class="font-bold text-sm text-gray-800">Permissions</span>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content class="px-1">
                      <div class="flex justify-end mb-2">
                        <v-btn
                          :color="$vuetify.theme.currentTheme.primary"
                          class="white--text text-xs"
                          @click="rolesPage.toggleAllPermissions"
                          small
                          depressed
                        >
                          {{ rolesPage.allPermissionsText }}
                        </v-btn>
                      </div>
                      <div
                        class="mb-2 text-xs text-red-600 font-semibold"
                        v-if="rolesPage.isFormSubmitted && !rolesPage.areAnyPermissionsSelected"
                      >
                        Please select at least one permission.
                      </div>

                      <!-- Mobile Permission Cards -->
                      <div class="flex flex-col gap-2">
                        <template v-for="item in rolesPage.filteredPermissions">
                          <!-- Section Header -->
                          <div
                            v-if="item.isHeader"
                            :key="item.name"
                            class="bg-gray-100 px-3 py-1 font-bold text-xs text-gray-700 rounded mt-2 border-b"
                          >
                            {{ item.name }}
                          </div>
                          <!-- Permission Item Card -->
                          <v-card
                            v-else
                            :key="item.key"
                            outlined
                            class="pa-3 rounded-lg border border-gray-100"
                          >
                            <div class="font-bold text-xs text-gray-800 mb-2">{{ item.name }}</div>
                            <div class="grid grid-cols-2 gap-2">
                              <v-checkbox
                                v-model="item.read"
                                label="View"
                                :color="$vuetify.theme.currentTheme.primary"
                                dense
                                hide-details
                                class="mt-0 pt-0"
                              ></v-checkbox>
                              <v-checkbox
                                v-model="item.write"
                                label="Create"
                                :disabled="rolesPage.checkIsReadChecked(item)"
                                :color="$vuetify.theme.currentTheme.primary"
                                dense
                                hide-details
                                class="mt-0 pt-0"
                              ></v-checkbox>
                              <v-checkbox
                                v-model="item.edit"
                                label="Edit"
                                :disabled="rolesPage.checkIsReadChecked(item)"
                                :color="$vuetify.theme.currentTheme.primary"
                                dense
                                hide-details
                                class="mt-0 pt-0"
                              ></v-checkbox>
                              <v-checkbox
                                v-model="item.delete"
                                label="Delete"
                                :disabled="rolesPage.checkIsReadChecked(item)"
                                :color="$vuetify.theme.currentTheme.primary"
                                dense
                                hide-details
                                class="mt-0 pt-0"
                              ></v-checkbox>
                            </div>
                            <div class="mt-2 flex justify-end">
                              <v-chip
                                small
                                dense
                                @click="rolesPage.toggleSelectAllPermissions(item)"
                                class="cursor-pointer text-[10px]"
                              >
                                {{
                                  rolesPage.isAllPermissionsSelected(item)
                                    ? 'Unselect All'
                                    : 'Select All'
                                }}
                              </v-chip>
                            </div>
                          </v-card>
                        </template>
                      </div>
                    </v-expansion-panel-content>
                  </v-expansion-panel>

                  <!-- 3. Module Permissions -->
                  <v-expansion-panel class="my-2 border border-gray-200 rounded-lg">
                    <v-expansion-panel-header class="py-2">
                      <span class="font-bold text-sm text-gray-800">Module Permissions</span>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content class="px-1">
                      <!-- Mobile Module Permission Cards -->
                      <div class="flex flex-col gap-3">
                        <v-card
                          v-for="item in rolesPage.modulePermissionsList"
                          :key="item.key"
                          outlined
                          class="pa-3 rounded-lg border border-gray-100"
                        >
                          <div class="font-bold text-xs text-gray-800 mb-2">{{ item.name }}</div>
                          <div class="grid grid-cols-2 gap-2">
                            <v-checkbox
                              v-model="item.read"
                              label="Read"
                              :color="$vuetify.theme.currentTheme.primary"
                              @change="rolesPage.checkFormChanges"
                              dense
                              hide-details
                              class="mt-0 pt-0"
                            ></v-checkbox>
                            <v-checkbox
                              v-model="item.add"
                              label="Add"
                              :color="$vuetify.theme.currentTheme.primary"
                              @change="rolesPage.checkFormChanges"
                              dense
                              hide-details
                              class="mt-0 pt-0"
                            ></v-checkbox>
                            <v-checkbox
                              v-model="item.edit"
                              label="Edit"
                              :color="$vuetify.theme.currentTheme.primary"
                              @change="rolesPage.checkFormChanges"
                              dense
                              hide-details
                              class="mt-0 pt-0"
                            ></v-checkbox>
                            <v-checkbox
                              v-model="item.delete"
                              label="Delete"
                              :color="$vuetify.theme.currentTheme.primary"
                              @change="rolesPage.checkFormChanges"
                              dense
                              hide-details
                              class="mt-0 pt-0"
                            ></v-checkbox>
                          </div>
                          <div class="mt-2 flex justify-end">
                            <v-chip
                              small
                              dense
                              @click="rolesPage.toggleSelectAllModulePermissions(item)"
                              class="cursor-pointer text-[10px]"
                            >
                              {{
                                rolesPage.isAllModulePermissionsSelected(item)
                                  ? 'Unselect All'
                                  : 'Select All'
                              }}
                            </v-chip>
                          </div>
                        </v-card>
                      </div>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-card-text>
            </div>
          </template>
        </ActitiveTabcomponent>

        <!-- Save/Cancel Footer Buttons (Pinned at Bottom) -->
        <div class="border-t px-3 py-3 bg-white fixed bottom-0 left-0 right-0 z-20">
          <div class="flex justify-end gap-x-2">
            <v-btn outlined @click="rolesPage.closeForm" class="normal-case text-xs">Cancel</v-btn>
            <v-btn
              v-if="!rolesPage.isEditingRole"
              :color="$vuetify.theme.currentTheme.primary"
              class="normal-case white--text text-xs"
              depressed
              @click="rolesPage.saveNewRole"
            >
              <v-icon left size="14">mdi-content-save</v-icon>
              Save
            </v-btn>
            <v-btn
              v-if="rolesPage.isEditingRole"
              :color="$vuetify.theme.currentTheme.primary"
              class="normal-case white--text text-xs"
              depressed
              @click="rolesPage.updateRole"
              :disabled="rolesPage.isUpdateButtonDisabled"
            >
              Update
            </v-btn>
          </div>
        </div>
      </v-card>
    </v-dialog>

    <!-- Dialog for AWS IAM statement json builder and copyable keys (Fullscreen on Mobile) -->
    <v-dialog v-model="rolesPage.showIamJsonDialog" fullscreen persistent>
      <v-card class="d-flex flex-column h-full pa-3">
        <div class="flex justify-between items-center mb-2">
          <span class="font-bold text-sm text-gray-800">IAM Policy Builder</span>
          <v-btn icon @click="rolesPage.showIamJsonDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="flex-grow-1 overflow-y-auto pb-16">
          <!-- Left Side: Granular Permission Directory -->
          <div class="mb-4">
            <label class="text-[10px] uppercase font-bold text-gray-400 block mb-1"
              >Select Module</label
            >
            <v-select
              v-model="rolesPage.selectedIamModuleFilter"
              :items="rolesPage.iamModuleOptions"
              item-text="label"
              item-value="key"
              outlined
              dense
              class="mb-3"
            >
              <template v-slot:item="{ item }">
                <div
                  v-if="item.isHeader"
                  class="font-bold text-gray-700 py-1 border-b w-full pointer-events-none text-xs"
                >
                  {{ item.label }}
                </div>
                <div
                  v-else
                  :class="item.parentKey ? 'pl-4 text-gray-600' : 'font-semibold'"
                  class="text-xs"
                >
                  {{ item.label }}
                </div>
              </template>
            </v-select>

            <div class="border rounded p-2 bg-gray-50 max-h-[40vh] overflow-y-auto">
              <v-list dense class="bg-transparent pa-0">
                <template v-for="(opt, idx) in rolesPage.filteredIamPermissionOptions">
                  <v-list-item :key="idx" class="border-b py-2 px-1 flex-col align-start">
                    <div class="w-full">
                      <div class="font-bold text-xs text-gray-900">{{ opt.label }}</div>
                      <div class="font-mono text-[10px] text-blue-600 mt-0.5">
                        {{ opt.displayKey || opt.key }}
                      </div>
                      <div class="text-[11px] text-gray-500 mt-1" style="white-space: normal">
                        {{ opt.description }}
                      </div>
                    </div>
                    <div class="flex gap-2 mt-2 w-full justify-end">
                      <v-btn
                        v-if="!rolesPage.isActionConfigured(opt.key, 'allow')"
                        x-small
                        outlined
                        color="success"
                        class="normal-case font-bold"
                        @click="rolesPage.toggleActionKey(opt.key, 'allow', true)"
                      >
                        Allow
                      </v-btn>
                      <v-btn
                        v-else
                        x-small
                        depressed
                        color="success"
                        class="normal-case font-bold white--text"
                        @click="rolesPage.toggleActionKey(opt.key, 'allow', false)"
                      >
                        Allowed <v-icon right x-small>mdi-close</v-icon>
                      </v-btn>

                      <v-btn
                        v-if="!rolesPage.isActionConfigured(opt.key, 'deny')"
                        x-small
                        outlined
                        color="error"
                        class="normal-case font-bold"
                        @click="rolesPage.toggleActionKey(opt.key, 'deny', true)"
                      >
                        Deny
                      </v-btn>
                      <v-btn
                        v-else
                        x-small
                        depressed
                        color="error"
                        class="normal-case font-bold white--text"
                        @click="rolesPage.toggleActionKey(opt.key, 'deny', false)"
                      >
                        Denied <v-icon right x-small>mdi-close</v-icon>
                      </v-btn>
                    </div>
                  </v-list-item>
                </template>
              </v-list>
            </div>
          </div>

          <!-- Right Side: JSON Editor Preview -->
          <div class="mt-4">
            <h4 class="font-semibold text-xs text-gray-800 mb-1">Statements Preview</h4>
            <v-textarea
              v-model="rolesPage.rawIamJsonString"
              outlined
              readonly
              rows="10"
              class="font-mono text-xs bg-gray-50"
              placeholder="IAM statements JSON will auto-generate here..."
              :error-messages="rolesPage.jsonParseError"
              hide-details
            ></v-textarea>
          </div>
        </div>

        <div
          class="border-t px-3 py-3 bg-white fixed bottom-0 left-0 right-0 z-20 flex justify-between"
        >
          <v-btn
            outlined
            color="error"
            class="text-xs normal-case"
            @click="rolesPage.resetJsonStatements"
            >Reset</v-btn
          >
          <v-btn
            color="primary"
            class="white--text text-xs normal-case"
            depressed
            :disabled="!!rolesPage.jsonParseError"
            @click="rolesPage.applyJsonStatements"
          >
            Apply JSON
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <AlertPopUp
      :showConfirmationDialog="rolesPage.showDeleteDialog"
      :confirmationTitle="'Confirm Delete'"
      :confirmationMessage="`Do you want to delete selected roles?`"
      :confirmationActionText="'DELETE'"
      :confirmationActionColor="'red'"
      :performAction="rolesPage.deleteSelectedRoles"
      :cancel="rolesPage.handleShowDialogDelete"
      :loading="rolesPage.loading"
    />

    <!-- Previlage Table presentation wrapper -->
    <PrevilageTable
      @edit-role="rolesPage.editRole"
      @configure-iam="rolesPage.openIamConfigDialog"
      @role-deleted="rolesPage.handleRoleDeleted"
      @update:options="rolesPage.handleTableOptionsUpdate"
      ref="previlageTable"
      :roles="rolesPage.roles"
      :loadTable="rolesPage.loadTable"
      :totalRoles="rolesPage.totalRoles"
      v-model="rolesPage.selectedRoles"
    />
  </div>
</template>

<script>
import FloatingActionBar from '@/components/Workforce/CollectionManagement/CaseModules/FloatingActionBar.vue'
import ActitiveTabcomponent from '@/components/ActitiveTabcomponent.vue'
import PrevilageTable from './PrevilageTable.vue'
import AlertPopUp from '@/components/AlertPopUp.vue'

export default {
  name: 'Roles_mobile',
  components: {
    FloatingActionBar,
    ActitiveTabcomponent,
    PrevilageTable,
    AlertPopUp
  },
  inject: ['rolesPage']
}
</script>

<style scoped>
.form-content {
  padding: 8px;
  max-height: calc(100vh - 120px) !important;
  overflow-y: auto;
  background-color: #f8f9fa;
}
</style>
